import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Glass } from '@ui/glass/Glass'
import './casca.css'
import { TABS } from './tabs'
import { IR_PARA } from './teclas'
import {
  IconeAjustes,
  IconeCasa,
  IconeEtiqueta,
  IconeFluxo,
  IconeLista,
  IconeNota,
  IconeRelatorio,
  IconeSino,
  IconeTriagem,
} from './icones'

const ICONES = {
  casa: IconeCasa,
  fluxo: IconeFluxo,
  etiqueta: IconeEtiqueta,
  lista: IconeLista,
} as const

/**
 * A tecla de cada destino, **derivada** de `IR_PARA` — e não copiada.
 *
 * O trilho só MOSTRA a tecla; quem a escuta é o `Atalhos`, lendo `IR_PARA`.
 * Uma segunda tabela aqui ensinaria, no dia em que as duas divergissem, um
 * atalho que não funciona. Invertida da mesma fonte, ela não tem como.
 */
const TECLA: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(IR_PARA).map(([tecla, { rota }]) => [rota, tecla]),
)

/**
 * As telas que o celular alcança por dentro de outras, e o trilho põe à vista.
 *
 * Não é arquitetura nova: as cinco já são destinos globais — cada uma tem a
 * sua tecla em `IR_PARA` —, só que no celular moram atrás do Dashboard porque
 * a barra tem lugar para quatro. No desktop cabem, e mostrar onde estão é o
 * que ensina que existem.
 */
const OUTRAS = [
  { to: '/notas', label: 'Notas', Icone: IconeNota },
  { to: '/classificar', label: 'Classificar', Icone: IconeTriagem },
  { to: '/relatorios', label: 'Relatórios', Icone: IconeRelatorio },
  { to: '/notificacoes', label: 'Avisos', Icone: IconeSino },
  { to: '/ajustes', label: 'Ajustes', Icone: IconeAjustes },
] as const

/**
 * A navegação do desktop: um trilho de vidro na lateral, com tudo rotulado.
 *
 * ## Por que o rótulo aparece SEMPRE aqui, e não só na aba ativa
 *
 * Na barra do celular o rótulo é da aba ativa só por falta de largura — quatro
 * palavras numa barra de 375px encolhem até não se lerem (ver `TabBar`). No
 * desktop a falta não existe, e o argumento original da barra volta inteiro:
 * ícone puro é ilegível para quem abre o app uma vez por semana. É a segunda
 * pessoa da casa, a que não leu a especificação.
 *
 * ## A tecla à mostra
 *
 * Os atalhos `g` + letra existem desde a decisão 290, e só quem já sabia deles
 * os usava — a ajuda mora atrás de `?`. No desktop há teclado, e o trilho é o
 * lugar onde o atalho se ensina sozinho, ao lado do destino que ele abre.
 *
 * ## Mesmo vidro e mesma seleção da barra
 *
 * O ativo usa exatamente a pílula da aba ativa: véu da marca a 22%, anel a 38%,
 * texto na marca. Duas navegações com dois desenhos de "você está aqui" seriam
 * dois aplicativos.
 */
export function Trilho() {
  return (
    <nav aria-label="Navegação principal" className="trilho">
      <Glass level="frost" className="trilho-vidro">
        <p className="titulo trilho-marca">Porquinho</p>

        <ul className="flex flex-col gap-1">
          {TABS.map((tab) => {
            const Icone = ICONES[tab.icone]
            return (
              <li key={tab.to}>
                <ItemDoTrilho to={tab.to} label={tab.label}>
                  <Icone />
                </ItemDoTrilho>
              </li>
            )
          })}
        </ul>

        <hr className="trilho-divisoria" />

        <ul className="flex flex-col gap-1">
          {OUTRAS.map(({ to, label, Icone }) => (
            <li key={to}>
              <ItemDoTrilho to={to} label={label}>
                <Icone />
              </ItemDoTrilho>
            </li>
          ))}
        </ul>
      </Glass>
    </nav>
  )
}

function ItemDoTrilho({
  to,
  label,
  children,
}: {
  to: string
  label: string
  children: ReactNode
}) {
  const tecla = TECLA[to]
  return (
    <Link
      to={to}
      className="item-do-trilho alvo"
      // Os mesmos dois cuidados da barra: `/` só acende exato, e a busca não
      // conta — `/transacoes?mes=2026-08` é o estado normal daquela tela.
      activeOptions={{ exact: to === '/', includeSearch: false }}
      activeProps={{ 'aria-current': 'page' }}
    >
      {children}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {tecla !== undefined && (
        // `aria-hidden`: o nome do link é o destino. "Transações g t" no leitor
        // de tela seria ruído no meio do nome.
        <kbd aria-hidden="true" className="trilho-tecla">
          g {tecla}
        </kbd>
      )}
    </Link>
  )
}
